import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiError, TOKEN_KEY } from "@/lib/api";
import { Loader2, Lock, ArrowLeft } from "lucide-react";

export default function AdminLogin() {
    const navigate = useNavigate();
    const [email, setEmail] = React.useState("");
    const [password, setPassword] = React.useState("");
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (window.localStorage.getItem(TOKEN_KEY)) {
            navigate("/admin", { replace: true });
        }
    }, [navigate]);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/auth/login", {
                email,
                password,
            });
            window.localStorage.setItem(TOKEN_KEY, data.access_token);
            toast.success("Welcome back, " + (data.user?.name || "Admin"));
            navigate("/admin", { replace: true });
        } catch (err) {
            toast.error(
                formatApiError(err?.response?.data?.detail) ||
                    "Login failed. Check credentials.",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#faf9f6] flex flex-col">
            <div className="mx-auto max-w-5xl w-full px-6 py-6">
                <Link
                    to="/"
                    data-testid="admin-back-home"
                    className="inline-flex items-center gap-2 text-sm text-[#4a5250] hover:text-[#114b5f]"
                >
                    <ArrowLeft size={14} /> Back to site
                </Link>
            </div>

            <div className="flex-1 flex items-center justify-center px-6 pb-16">
                <div className="w-full max-w-md">
                    <div className="text-center mb-8">
                        <div className="mx-auto h-14 w-14 rounded-full bg-[#114b5f] text-[#faf9f6] flex items-center justify-center">
                            <Lock size={20} strokeWidth={1.5} />
                        </div>
                        <h1 className="mt-6 font-serif-display text-4xl text-[#1a1d1c]">
                            Staff Login
                        </h1>
                        <p className="mt-2 text-sm text-[#4a5250]">
                            Access the Galaxy Dental appointments dashboard.
                        </p>
                    </div>

                    <form
                        onSubmit={submit}
                        data-testid="admin-login-form"
                        className="bg-white rounded-3xl border border-[#e8e4db] p-8 space-y-5 shadow-sm"
                    >
                        <div>
                            <Label
                                htmlFor="admin-email"
                                className="text-xs uppercase tracking-wider text-[#4a5250]"
                            >
                                Email
                            </Label>
                            <Input
                                id="admin-email"
                                data-testid="admin-login-email-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="mt-1 bg-[#faf9f6] rounded-xl border-[#e8e4db]"
                            />
                        </div>
                        <div>
                            <Label
                                htmlFor="admin-password"
                                className="text-xs uppercase tracking-wider text-[#4a5250]"
                            >
                                Password
                            </Label>
                            <Input
                                id="admin-password"
                                data-testid="admin-login-password-input"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="mt-1 bg-[#faf9f6] rounded-xl border-[#e8e4db]"
                            />
                        </div>
                        <button
                            type="submit"
                            data-testid="admin-login-button"
                            disabled={loading}
                            className="w-full rounded-full bg-[#114b5f] text-[#faf9f6] px-6 py-4 text-sm font-medium hover:bg-[#0d3a4a] disabled:opacity-60 flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <Loader2
                                        className="animate-spin"
                                        size={16}
                                    />
                                    Signing in…
                                </>
                            ) : (
                                "Sign in"
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
